import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useExports } from '../hooks/useExports';
import { ExportHistoryList } from './ExportHistoryList';
import { useRoles } from '@/core/auth/useRoles';

type ExportModalProps = {
  open: boolean;
  onClose: () => void;
  studyId: string;
  study?: { name?: string; publishedBy?: string };
};

export const ExportModal: React.FC<ExportModalProps> = ({
  open,
  onClose,
  studyId,
  study,
}) => {
  const { t } = useTranslation();
  const { data: rolesData } = useRoles();
  const { exportJobs, isCreatingExport, startExport, downloadExport } =
    useExports(studyId);

  const isAdmin = rolesData?.some((role) => role.name === 'admin');

  // Check if current user is the study publisher
  const currentUserId = rolesData?.[0]?.id;
  const isStudyPublisher =
    study && currentUserId && study.publishedBy === currentUserId;

  // Show export button only if user is admin or study publisher
  const canExport = isAdmin || isStudyPublisher;

  const handleStartExport = async () => {
    await startExport();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle>
        {t('pages.studies.export.title', { studyName: study?.name })}
      </DialogTitle>
      <DialogContent>
        {canExport && (
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <LoadingButton
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleStartExport}
              loading={isCreatingExport}
              size="large"
              sx={{ px: 4 }}
            >
              {t('buttons.export')}
            </LoadingButton>
          </Box>
        )}

        <ExportHistoryList
          exportJobs={exportJobs}
          downloadExport={downloadExport}
          isAdmin={isAdmin}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('buttons.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
